import { SEOConfig } from '@/types/theme';

/**
 * Applies SEO settings to the document head
 * @param seoSettings - The SEO configuration to apply
 * @param tenantName - Optional tenant name for fallback defaults
 */
export const applySEOSettings = (seoSettings: SEOConfig | null, tenantName?: string) => {
  if (!seoSettings) {
    // If no SEO settings provided, apply default theme SEO with tenant-aware fallback
    const defaultTitle = tenantName ? `${tenantName} Project Management` : 'Project Management Platform';
    const defaultDescription = 'Professional solar project management platform for installers and project managers.';
    
    const defaultSEO: SEOConfig = {
      pageTitle: defaultTitle,
      metaDescription: defaultDescription,
      metaKeywords: 'solar, project management, installer, renewable energy, solar installation',
      ogTitle: defaultTitle,
      ogDescription: defaultDescription,
      ogImage: '/joules-logo.png',
      twitterCard: 'summary_large_image',
      twitterTitle: defaultTitle,
      twitterDescription: defaultDescription,
      twitterImage: '/joules-logo.png',
      canonicalUrl: '',
      robotsMeta: 'index, follow',
    };
    applySEOSettings(defaultSEO, tenantName);
    return;
  }

  // Update page title
  if (seoSettings.pageTitle) {
    document.title = seoSettings.pageTitle;
  }

  // Helper function to update or create meta tag
  const updateMetaTag = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  // Helper function to update or create property meta tag
  const updatePropertyMetaTag = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  // Basic SEO meta tags
  if (seoSettings.metaDescription) {
    updateMetaTag('description', seoSettings.metaDescription);
  }

  if (seoSettings.metaKeywords) {
    updateMetaTag('keywords', seoSettings.metaKeywords);
  }

  if (seoSettings.robotsMeta) {
    updateMetaTag('robots', seoSettings.robotsMeta);
  }

  // Canonical URL
  if (seoSettings.canonicalUrl) {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = seoSettings.canonicalUrl;
  }

  // Open Graph meta tags
  if (seoSettings.ogTitle) {
    updatePropertyMetaTag('og:title', seoSettings.ogTitle);
  }

  if (seoSettings.ogDescription) {
    updatePropertyMetaTag('og:description', seoSettings.ogDescription);
  }

  if (seoSettings.ogImage) {
    updatePropertyMetaTag('og:image', seoSettings.ogImage);
  }

  // Twitter Card meta tags
  if (seoSettings.twitterCard) {
    updateMetaTag('twitter:card', seoSettings.twitterCard);
  }

  if (seoSettings.twitterTitle) {
    updateMetaTag('twitter:title', seoSettings.twitterTitle);
  }

  if (seoSettings.twitterDescription) {
    updateMetaTag('twitter:description', seoSettings.twitterDescription);
  }

  if (seoSettings.twitterImage) {
    updateMetaTag('twitter:image', seoSettings.twitterImage);
  }

};

/**
 * Resets SEO settings to default values
 */
export const resetSEOSettings = (tenantName?: string) => {
  // Reset page title to default with tenant-aware fallback
  const defaultTitle = tenantName ? `${tenantName} Project Management` : 'Project Management Platform';
  document.title = defaultTitle;

  // Remove custom meta tags
  const customMetaTags = document.querySelectorAll('meta[name="description"], meta[name="keywords"], meta[name="robots"], meta[property^="og:"], meta[name^="twitter:"]');
  customMetaTags.forEach(tag => tag.remove());

  // Remove canonical link
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.remove();
  }

}; 